/**
 * Store product extensions (storefront side).
 *
 * The admin registry (aldoo-ra/Admin/Store/extensions) gives an extension its
 * tab in the product dialog; this one gives it its place on the product page.
 * The server sends every product with `extensions: [ids]` — the enabled
 * extensions its content type lists, in STORE_PRODUCT_MODEL order — and the
 * page renders the slots of those ids only. A product without the list (or
 * with an id no storefront extension declares) renders no slot at all, so a
 * product page is what it always was until an extension is installed.
 *
 * An extension is a folder with a storefront.jsx exporting:
 *
 *   export const StorefrontExtension = {
 *     id: "options",                               // defaults to the folder name
 *     slots: {                                     // each a component, or an array of them
 *       selectors,                                 // the purchase area, above the buy button
 *       siblings,                                  // the purchase area, above the selectors
 *       belowPrice,                                // under the price
 *       badges,                                    // over the gallery
 *       recommendations,                           // before the page's computed recommendations
 *       tabs,                                      // { id, label, component, visible? } (or an array): an extra tab;
 *                                                  // visible(product) false: not shown for this product
 *     },
 *     isComplete: (product, selection) => boolean, // optional, default true: may the buyer buy?
 *     missing: (product, selection) => [{ key, label }], // optional: the choices still to make,
 *                                                  // named for the buyer ("Размер")
 *     price: (product, selection, line) => line,   // optional: the client mirror of the server's
 *                                                  // resolveLine — display only, the server prices
 *   }
 *
 * The slot components are rendered with { product, selection, setSelection,
 * line, isComplete, missing, attempted }. `attempted` turns true when the
 * buyer pressed Buy with choices `missing`: a selector marks its own then. `selection` is the extensions' shared state of the page
 * (free-form; every extension keeps its own keys in it); `line` is what the
 * selection resolves to — { pid, sku, amount, currency, label, choices,
 * choiceLabels, components } — through every extension's `price`, in order,
 * from the product's own price. The buy button sends the line's sku, choices,
 * choiceLabels and components with the cart line, and the server resolves
 * them again (aldoo-na/store/lines.js resolveLine) — the client's price is
 * never used.
 *
 * The framework's own storefront extensions live here
 * (aldoo-ra/Store/extensions/<id>/storefront.jsx); a project keeps its own in
 * /components/Store/extensions/<id>/storefront.jsx, next to its admin half.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react"

const extensions = new Map()

/** Declare a storefront extension (called by the discovery below, or by a project by hand). */
export const registerStorefrontExtension = (definition) => {
  if (!definition?.id) throw new Error("registerStorefrontExtension: an id is required")
  extensions.set(definition.id, definition)
  return definition
}

export const getStorefrontExtension = (id) => extensions.get(id) || null
export const getStorefrontExtensions = () => [...extensions.values()]

/**
 * The storefront extensions that apply to a product: the ids the server sent
 * (`product.extensions`), in that order, that a storefront extension declares.
 */
export const storefrontExtensionsOf = (product) => {
  const ids = Array.isArray(product?.extensions) ? product.extensions : []
  return ids.map((id) => extensions.get(id)).filter(Boolean)
}

const isComplete = (applicable, product, selection) =>
  applicable.every((extension) => {
    if (typeof extension.isComplete !== "function") return true
    try {
      return extension.isComplete(product, selection) !== false
    } catch {
      return false
    }
  })

/**
 * Does the product need a choice before it can be bought? A product card asks
 * this to send the buyer to the product page instead of adding a line it
 * cannot complete. A list's product carries no options or parts (only the
 * server's cheap list fields), so the server's `needsSelection` flag answers
 * there; a product with its page's fields is asked of its extensions.
 */
export const needsSelection = (product) => product?.needsSelection === true || !isComplete(storefrontExtensionsOf(product), product, {})

/**
 * The line a product starts from, before any extension priced it — the same
 * start the server's resolveLine takes. `amount` is in minor units; a project
 * whose products carry their price elsewhere passes `baseLine` to the provider.
 */
export const defaultBaseLine = (product) => ({
  pid: product?.pid,
  sku: "",
  amount: Number.isFinite(product?.amount) ? product.amount : null,
  currency: product?.currency || null,
  label: product?.name || "",
  choices: {},
  choiceLabels: {},
  components: [],
})

/**
 * The selection line: the base line through each applicable extension's
 * `price`, in order. A `price` that throws means the selection does not
 * resolve: the line is marked invalid and the buy button stays off.
 */
const resolveLine = (applicable, product, selection, baseLine) => {
  let line = { ...baseLine(product), valid: true }
  for (const extension of applicable) {
    if (typeof extension.price !== "function") continue
    try {
      line = extension.price(product, selection, line) || line
    } catch (error) {
      return { ...line, valid: false, error: error?.message || String(error) }
    }
  }
  return line
}

/** What the buyer still has to choose, from every applicable extension's `missing`. */
const missingOf = (applicable, product, selection) =>
  applicable.flatMap((extension) => {
    if (typeof extension.missing !== "function") return []
    try {
      return extension.missing(product, selection) || []
    } catch {
      return []
    }
  })

const ProductSelectionContext = createContext(null)

/**
 * Holds the buyer's selection on a product page. Everything under it — the
 * slots, the buy button, the price — reads one selection, one resolved line
 * and one answer to "may the buyer buy".
 */
export function ProductSelectionProvider({ product, baseLine = defaultBaseLine, initialSelection, children }) {
  const pid = product?.pid
  const [state, setState] = useState(() => ({ pid, selection: initialSelection || {} }))
  // Buy was pressed with choices missing (per product): the selectors mark theirs
  const [attemptedPid, setAttemptedPid] = useState(null)
  const requestCompletion = useCallback(() => setAttemptedPid(pid), [pid])
  // another product on the same page instance starts from nothing
  const selection = state.pid === pid ? state.selection : initialSelection || {}

  /** A patch merged into the selection, or an updater (selection) => selection. */
  const setSelection = useCallback(
    (patch) =>
      setState((current) => {
        const before = current.pid === pid ? current.selection : initialSelection || {}
        const next = typeof patch === "function" ? patch(before) : { ...before, ...(patch || {}) }
        return { pid, selection: next || {} }
      }),
    [pid, initialSelection]
  )

  const applicable = useMemo(() => storefrontExtensionsOf(product), [product])
  const value = useMemo(() => {
    const line = resolveLine(applicable, product, selection, baseLine)
    const missing = missingOf(applicable, product, selection)
    return {
      product,
      selection,
      setSelection,
      line,
      // an extension with a `price` decides what the page shows
      priced: applicable.some((extension) => typeof extension.price === "function"),
      isComplete: line.valid !== false && isComplete(applicable, product, selection),
      missing,
      attempted: attemptedPid === pid && missing.length > 0,
      requestCompletion,
      extensions: applicable,
    }
  }, [applicable, product, selection, setSelection, baseLine, attemptedPid, pid, requestCompletion])

  return React.createElement(ProductSelectionContext.Provider, { value }, children)
}

/**
 * The page's selection: { selection, setSelection, line, isComplete, priced,
 * missing, attempted, requestCompletion, extensions }, or null outside a ProductSelectionProvider (or under one of
 * another product) — a caller then behaves as it did before extensions.
 */
export const useProductSelection = (product) => {
  const context = useContext(ProductSelectionContext)
  if (!context) return null
  if (product?.pid && context.product?.pid !== product.pid) return null
  return context
}

const asList = (value) => (Array.isArray(value) ? value : value ? [value] : [])

/**
 * The tabs the product's extensions add: [{ id, label, component, extension }].
 * A bare component is a tab labelled by the extension's `label`.
 */
export const extensionTabsOf = (product) =>
  storefrontExtensionsOf(product).flatMap((extension) =>
    asList(extension.slots?.tabs).map((tab, index) => {
      const component = typeof tab === "function" ? tab : tab?.component
      return {
        id: (typeof tab === "object" && tab?.id) || `${extension.id}${index ? `-${index}` : ""}`,
        label: (typeof tab === "object" && tab?.label) || extension.label || extension.id,
        component,
        extension: extension.id,
        visible: typeof tab === "object" && typeof tab?.visible === "function" ? tab.visible : null,
      }
    }).filter((tab) => tab.component && (!tab.visible || tab.visible(product)))
  )

/**
 * Renders slot `name` of the product's extensions, in order. Nothing — not
 * even the wrapper — when no extension fills it. `className` wraps the slot's
 * components in a div; without it they render bare.
 */
export function ExtensionSlot({ name, product, className, ...rest }) {
  const context = useProductSelection(product)
  const subject = product || context?.product
  const components = storefrontExtensionsOf(subject).flatMap((extension) =>
    asList(extension.slots?.[name]).map((entry, index) => ({
      key: `${extension.id}-${index}`,
      Component: typeof entry === "function" ? entry : entry?.component,
    }))
  ).filter((entry) => entry.Component)
  if (!components.length) return null

  const props = {
    ...rest,
    product: subject,
    selection: context?.selection || {},
    setSelection: context?.setSelection || (() => {}),
    line: context?.line || null,
    isComplete: context?.isComplete ?? true,
    missing: context?.missing || [],
    attempted: !!context?.attempted,
  }
  const rendered = components.map(({ key, Component }) => React.createElement(Component, { key, ...props }))
  if (!className) return React.createElement(React.Fragment, null, rendered)
  return React.createElement("div", { className, "data-slot": name }, rendered)
}

// The framework's own storefront extensions, and the ones the project keeps in
// /components/Store/extensions — the glob resolves at build time and is empty
// when no folder has a storefront half.
const modules = {
  ...import.meta.glob("./*/storefront.jsx", { eager: true }),
  ...import.meta.glob("/components/Store/extensions/*/storefront.jsx", { eager: true }),
}

for (const [path, module] of Object.entries(modules)) {
  const definition = module.StorefrontExtension || module.default
  if (!definition) continue
  registerStorefrontExtension({ id: path.split("/").at(-2), ...definition })
}
